
import React from 'react';
import { CalendarViewMode } from '../../types';
import { useLanguage } from '@/contexts/LanguageContext';
import CalendarStandardView from '../CalendarStandardView';
import TotalWorkView from '../TotalWorkView';
import { startOfMonth, endOfMonth } from 'date-fns';
import { useShiftContextMenu } from '../../hooks/useShiftContextMenu';
import { generateDaySchedules } from './utils';

interface CalendarViewRendererProps {
  viewMode: CalendarViewMode;
  currentDate: Date;
  filteredSchedules: any;
  dateRange: any;
  isCustomView: boolean;
  isAdmin: boolean;
  guards: any;
  selectedGuardId: string;
  guardSchedules: any;
  allGuardsSchedules: any;
  isLoadingGuardSchedules: boolean;
  user: any;
  permissions: any;
  handleDayClick: (date: Date) => void;
  handleEventClick: (event: any, e: React.MouseEvent, isMultiSelectMode?: boolean) => void;
  onDuplicateEvent: (event: any) => void;
  onDeleteEvent: (event: any) => void;
  handleGuardSelect: (guardId: string) => void;
  onMarkForSwap?: (event: any) => void;
  onSwapWithMarked?: (event: any) => void;
  onCancelSwap?: () => void;
  markedShiftId?: string | null;
  onDragEnd?: (result: any) => void;
  viewType?: 'standard' | 'per-job';
  calendarZoom?: number;
}

const CalendarViewRenderer: React.FC<CalendarViewRendererProps> = (props) => {
  const { t } = useLanguage();
  const { copyShift, copyShiftWithoutGuards, pasteShift, deleteShift, hasCopiedShift } = useShiftContextMenu();
  
  const {
    viewMode,
    currentDate,
    filteredSchedules,
    dateRange,
    isCustomView,
    guards,
    selectedGuardId,
    guardSchedules,
    allGuardsSchedules,
    isLoadingGuardSchedules,
    isAdmin,
    user,
    permissions,
    handleDayClick,
    handleEventClick,
    onDuplicateEvent,
    onDeleteEvent,
    handleGuardSelect,
    onMarkForSwap,
    onSwapWithMarked,
    onCancelSwap,
    markedShiftId,
    viewType = 'standard',
    calendarZoom = 0.9
  } = props;
  
  console.log('CalendarViewRenderer - filteredSchedules:', {
    count: filteredSchedules?.length || 0,
    viewType,
    viewMode,
    dateRange,
    schedules: filteredSchedules
  });
  
  switch (viewMode) {
    case 'total-work':
      return (
        <TotalWorkView
          dateRange={dateRange}
          guards={guards}
          selectedGuardId={selectedGuardId}
          guardSchedules={guardSchedules}
          allGuardsSchedules={allGuardsSchedules}
          isLoadingGuardSchedules={isLoadingGuardSchedules}
          isAdmin={isAdmin}
          currentDate={currentDate}
          user={user}
          handleGuardSelect={handleGuardSelect}
          handleDayClick={handleDayClick}
          handleEventClick={handleEventClick}
          calendarZoom={calendarZoom}
        />
      );
    
    case 'month':
      // For month view, use the entire month date range
      const monthRange = {
        start: startOfMonth(currentDate),
        end: endOfMonth(currentDate)
      };
      
      console.log('Month view date range:', { 
        start: monthRange.start.toISOString(),
        end: monthRange.end.toISOString()
      });
      
      const monthDaySchedules = generateDaySchedules(
        monthRange.start, 
        monthRange.end, 
        filteredSchedules
      );
      
      return (
        <CalendarStandardView
          daySchedules={monthDaySchedules}
          handleDayClick={handleDayClick}
          handleEventClick={handleEventClick}
          onDuplicateEvent={onDuplicateEvent}
          onDeleteEvent={onDeleteEvent}
          onCopyShift={copyShift}
          onCopyShiftWithoutGuards={copyShiftWithoutGuards}
          onPasteShift={pasteShift}
          onMarkForSwap={onMarkForSwap}
          onSwapWithMarked={onSwapWithMarked}
          onCancelSwap={onCancelSwap}
          markedShiftId={markedShiftId}
          onCopyDay={(date, events, withGuards) => {
            console.log('Copy day:', date, events.length, withGuards);
          }}
          onPasteDay={async (date) => {
            console.log('Paste day at:', date);
            return [];
          }}
          onDeleteDay={(date, events) => {
            console.log('Delete day:', date, events.length);
            events.forEach(event => onDeleteEvent(event));
          }}
          permissions={permissions}
          isCustomDateView={false}
          isMonthView={true}
          monthViewColumnWidth="180px"
          viewType={viewType}
          guards={guards}
          calendarZoom={calendarZoom}
        />
      );
    
    default:
      console.log('CalendarViewRenderer - Default case (week/custom view):', {
        dateRange,
        filteredSchedulesCount: filteredSchedules?.length || 0,
        viewType
      });
      
      const daySchedules = generateDaySchedules(dateRange.start, dateRange.end, filteredSchedules);
      
      console.log('CalendarViewRenderer - Generated day schedules:', {
        daySchedulesCount: daySchedules.length,
        totalEvents: daySchedules.reduce((sum, day) => sum + day.events.length, 0),
        daySchedules: daySchedules.map(day => ({
          date: day.date.toISOString(),
          eventsCount: day.events.length
        }))
      });
      
      return (
        <div className="w-full overflow-hidden">
          <CalendarStandardView
            daySchedules={daySchedules}
            handleDayClick={handleDayClick}
            handleEventClick={handleEventClick}
            onDuplicateEvent={onDuplicateEvent}
            onDeleteEvent={onDeleteEvent}
            onCopyShift={copyShift}
            onCopyShiftWithoutGuards={copyShiftWithoutGuards}
            onPasteShift={pasteShift}
            onMarkForSwap={onMarkForSwap}
            onSwapWithMarked={onSwapWithMarked}
            onCancelSwap={onCancelSwap}
            markedShiftId={markedShiftId}
            onCopyDay={(date, events, withGuards) => {
              console.log('Copy day:', date, events.length, withGuards);
            }}
            onPasteDay={async (date) => {
              console.log('Paste day at:', date);
              return [];
            }}
            onDeleteDay={(date, events) => {
              console.log('Delete day:', date, events.length);
              events.forEach(event => onDeleteEvent(event));
            }}
            permissions={permissions}
            isCustomDateView={isCustomView}
            isMonthView={false}
            viewType={viewType}
            guards={guards}
            dateRange={dateRange}
            calendarZoom={calendarZoom}
          />
        </div>
      );
  }
};

export default CalendarViewRenderer;
